import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useEffect, useState } from "react";

import EmployerAnnualSavingsChart from "../../../components/charts/APCharts/employerAnnualSavings/EmployerAnnualSavingsChart";
import EmployerPayrollTaxSavingsChart from "../../../components/charts/APCharts/employerPayrollTaxSavings/EmployerPayrollTaxSavingsChart";
import EmployerStatsSummaries, {
  STAT_SHIFT_MS,
} from "../../../components/stats/employer/EmployerStatsSummaries";
import { isSafari } from "../../../helpers/browser";
import { EmployerStatsSummary } from "../../../types/Stats";

const ROTATE_MS = 6000;
const CHART_DELAY_MS = 0;
const CHART_HEIGHT = 368;

type InsightView = "employeeSavings" | "payrollTax";

const insightViewOrder: InsightView[] = ["employeeSavings", "payrollTax"];

const cycleInsightView = (
  view: InsightView,
  direction: 1 | -1
): InsightView => {
  const index = insightViewOrder.indexOf(view);
  const nextIndex =
    (index + direction + insightViewOrder.length) % insightViewOrder.length;

  return insightViewOrder[nextIndex];
};

const ChartCycleArrows = ({
  view,
  disabled = false,
  onSelect,
}: {
  view: InsightView;
  disabled?: boolean;
  onSelect: (next: InsightView) => void;
}) => {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <IconButton
        size="small"
        disabled={disabled}
        aria-label="Previous chart"
        onClick={() => onSelect(cycleInsightView(view, -1))}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        size="small"
        disabled={disabled}
        aria-label="Next chart"
        onClick={() => onSelect(cycleInsightView(view, 1))}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

const EmployerHomeInsights = ({
  stats,
  showPayrollTaxStats,
}: {
  stats: EmployerStatsSummary;
  showPayrollTaxStats: boolean;
}) => {
  const [view, setView] = useState<InsightView>("employeeSavings");
  const [chartView, setChartView] = useState<InsightView>(view);
  const [rotationKey, setRotationKey] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const chartHeight = showPayrollTaxStats ? CHART_HEIGHT : CHART_HEIGHT - 30;
  const _isSafari = isSafari();

  useEffect(() => {
    if (showPayrollTaxStats) {
      const timeoutId = window.setTimeout(
        () => setChartView(view),
        CHART_DELAY_MS
      );
      return () => window.clearTimeout(timeoutId);
    }
  }, [view, stats]);

  useEffect(() => {
    if (showPayrollTaxStats) {
      const timeoutId = window.setTimeout(() => {
        setView((current) => cycleInsightView(current, 1));
        setTransitioning(true);
      }, ROTATE_MS);

      return () => window.clearTimeout(timeoutId);
    }
  }, [view, rotationKey, stats]);

  useEffect(() => {
    if (!transitioning) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => setTransitioning(false),
      STAT_SHIFT_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [transitioning]);

  const selectView = (next: InsightView) => {
    if (next === view || transitioning) {
      return;
    }

    setView(next);
    setRotationKey((key) => key + 1);
    setTransitioning(true);
  };

  return (
    <Box py={1}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
          gridTemplateAreas: {
            xs: `"chart" "stats"`,
            sm: `"chart arrows" "chart stats"`,
          },
          gridTemplateRows: { sm: "auto 1fr" },
          columnGap: _isSafari ? 0 : 4,
          alignItems: "start",
        }}
      >
        {showPayrollTaxStats && (
          <Box
            sx={{
              gridArea: "arrows",
              justifySelf: "end",
              display: { xs: "none", sm: "block" },
            }}
          >
            <ChartCycleArrows
              view={view}
              disabled={transitioning}
              onSelect={selectView}
            />
          </Box>
        )}
        <Box
          sx={{ gridArea: "chart" }}
          mt={showPayrollTaxStats ? 0.5 : undefined}
        >
          {chartView === "employeeSavings" ? (
            <EmployerAnnualSavingsChart customHeight={chartHeight} />
          ) : (
            <EmployerPayrollTaxSavingsChart customHeight={chartHeight} />
          )}
        </Box>
        <Box sx={{ gridArea: "stats" }}>
          <EmployerStatsSummaries
            stats={stats}
            config={{ desktopLayout: "single" }}
            showAlternate={view === "payrollTax"}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EmployerHomeInsights;
